/**
 * V55.64: HR 면담 상세 페이지 (읽기 전용)
 *
 * HR에게 전송된 면담 기록의 스냅샷을 표시합니다.
 * - 읽기 전용 (수정/삭제 불가)
 * - 스냅샷 기반 표시 (전송 시점의 내용)
 *
 * @module pages/hr/HrInterviewDetailPage
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader, AlertTriangle, User, Calendar, Clock, MapPin, FileText, PenTool, Send } from 'lucide-react';
import { useHrInterviewShareDetail, HrInterviewShareDetail } from '../../shared/hooks/useHrInterviewShares';
import { getDownloadURL, ref } from 'firebase/storage';
import { formatDateTimeByCompanyCode } from '../../shared/utils/dateUtils';  // V62
import { storage } from '../../firebase/config';

// ============================================
// 메인 컴포넌트
// ============================================

export function HrInterviewDetailPage() {
  const { shareId } = useParams<{ shareId: string }>();
  const navigate = useNavigate();
  const { share, isLoading, error } = useHrInterviewShareDetail(shareId);

  // 뒤로가기
  const handleBack = () => {
    navigate('/hr/performance');
  };

  // 로딩 상태
  if (isLoading) {
    return (
      <div style={{ padding: '24px' }}>
        <BackButton onClick={handleBack} />
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '100px',
            color: 'var(--color-gray-500)',
          }}
        >
          <Loader size={32} style={{ animation: 'spin 1s linear infinite' }} />
          <span style={{ marginLeft: '12px', fontSize: '16px' }}>로딩 중...</span>
        </div>
      </div>
    );
  }

  // 에러 상태
  if (error || !share) {
    return (
      <div style={{ padding: '24px' }}>
        <BackButton onClick={handleBack} />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '100px',
            color: 'var(--color-red-600)',
            background: 'var(--color-red-50)',
            borderRadius: '8px',
            marginTop: '24px',
          }}
        >
          <AlertTriangle size={48} style={{ marginBottom: '16px' }} />
          <p style={{ fontSize: '16px', marginBottom: '8px' }}>
            {error?.message || '면담 기록을 찾을 수 없습니다.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }}>
      {/* 뒤로가기 + 읽기전용 배지 */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <BackButton onClick={handleBack} />
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            background: 'var(--color-blue-50)',
            color: 'var(--color-blue-700)',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 500,
          }}
        >
          <FileText size={14} />
          읽기 전용 (스냅샷)
        </span>
      </div>

      {/* 메인 카드 */}
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          border: '1px solid var(--color-gray-200)',
          overflow: 'hidden',
        }}
      >
        {/* 헤더 */}
        <div
          style={{
            padding: '20px 24px',
            background: 'var(--color-gray-50)',
            borderBottom: '1px solid var(--color-gray-200)',
          }}
        >
          <h1 style={{ fontSize: '20px', fontWeight: 600, color: 'var(--color-gray-800)', marginBottom: '4px' }}>
            {share.interviewTypeLabel}
          </h1>
          <p style={{ fontSize: '14px', color: 'var(--color-gray-500)' }}>
            {share.targetUserName} 직원의 면담 기록
          </p>
        </div>

        {/* 메타 정보 */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--color-gray-100)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
            <InfoItem icon={<User size={16} />} label="면담 대상자" value={`${share.targetUserName} (${share.targetOrgUnitName})`} />
            <InfoItem icon={<User size={16} />} label="면담자(조직장)" value={share.interviewerName} />
            <InfoItem icon={<Calendar size={16} />} label="면담일" value={share.interviewDate} />
            <InfoItem icon={<Clock size={16} />} label="면담시간" value={share.interviewTime} />
            <InfoItem icon={<MapPin size={16} />} label="면담 장소" value={share.location} />
            <InfoItem
              icon={<FileText size={16} />}
              label="상태"
              value={
                <span
                  style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 500,
                    background: 'var(--color-green-50)',
                    color: 'var(--color-green-600)',
                  }}
                >
                  {share.deliveryStatusLabel}
                </span>
              }
            />
          </div>
        </div>

        {/* 면담 내용 */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--color-gray-100)' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--color-gray-700)', marginBottom: '12px' }}>
            면담 내용
          </h3>
          <div
            style={{
              padding: '16px',
              background: 'var(--color-gray-50)',
              borderRadius: '8px',
              fontSize: '14px',
              lineHeight: '1.6',
              color: 'var(--color-gray-700)',
            }}
            dangerouslySetInnerHTML={{ __html: share.contentHtml || '<p>내용 없음</p>' }}
          />
        </div>

        {/* 서명 영역 */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--color-gray-100)' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--color-gray-700)', marginBottom: '16px' }}>
            서명
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px' }}>
            <SignatureBox
              label="면담자 서명"
              signaturePath={share.interviewerSignaturePath}
              signerName={share.interviewerName}
            />
            <SignatureBox
              label="대상자 서명"
              signaturePath={share.targetSignaturePath}
              signerName={share.targetUserName}
            />
          </div>
        </div>

        {/* 전송 정보 */}
        <div style={{ padding: '20px 24px', background: 'var(--color-gray-50)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--color-gray-500)', fontSize: '13px' }}>
            <Send size={14} />
            <span>
              {/* 정책 (V62-3): share.companyCode(면담 대상 회사) 기준 시차.
                  행위자(전송자) 기준이 아님. 동일 면담 기록 내 시간 기준 통일 목적. */}
              {share.sentByName}님이 {formatDateTimeByCompanyCode(share.sentAt, share.companyCode)}에 HR에 전송
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// 서브 컴포넌트
// ============================================

interface BackButtonProps {
  onClick: () => void;
}

function BackButton({ onClick }: BackButtonProps) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 12px',
        fontSize: '14px',
        color: 'var(--color-gray-600)',
        background: 'none',
        border: '1px solid var(--color-gray-300)',
        borderRadius: '6px',
        cursor: 'pointer',
      }}
    >
      <ArrowLeft size={16} />
      목록으로
    </button>
  );
}

interface InfoItemProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}

function InfoItem({ icon, label, value }: InfoItemProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
      <div style={{ color: 'var(--color-gray-400)', marginTop: '2px' }}>{icon}</div>
      <div>
        <div style={{ fontSize: '12px', color: 'var(--color-gray-500)', marginBottom: '2px' }}>{label}</div>
        <div style={{ fontSize: '14px', color: 'var(--color-gray-800)' }}>{value}</div>
      </div>
    </div>
  );
}

interface SignatureBoxProps {
  label: string;
  signaturePath?: string;
  signerName: string;
}

function SignatureBox({ label, signaturePath, signerName }: SignatureBoxProps) {
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!signaturePath || !storage) {
      setSignatureUrl(null);
      return;
    }

    // storage를 로컬 변수에 캡처하여 async 콜백에서 non-null로 사용
    const storageInstance = storage;

    const loadSignature = async () => {
      try {
        const signatureRef = ref(storageInstance, signaturePath);
        const url = await getDownloadURL(signatureRef);
        setSignatureUrl(url);
      } catch (err) {
        console.error('[V55.64] Signature load error:', err);
        setLoadError(true);
      }
    };

    loadSignature();
  }, [signaturePath]);

  return (
    <div>
      <div style={{ fontSize: '12px', color: 'var(--color-gray-500)', marginBottom: '8px' }}>{label}</div>
      <div
        style={{
          padding: '16px',
          background: 'white',
          border: '1px solid var(--color-gray-200)',
          borderRadius: '8px',
          minHeight: '80px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {signatureUrl ? (
          <img
            src={signatureUrl}
            alt={`${signerName} 서명`}
            style={{ maxWidth: '100%', maxHeight: '80px', objectFit: 'contain' }}
          />
        ) : loadError ? (
          <div style={{ color: 'var(--color-gray-400)', fontSize: '13px' }}>서명 로드 실패</div>
        ) : signaturePath ? (
          <Loader size={20} style={{ color: 'var(--color-gray-400)', animation: 'spin 1s linear infinite' }} />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--color-gray-400)', fontSize: '13px' }}>
            <PenTool size={16} />
            서명 없음
          </div>
        )}
      </div>
      <div style={{ fontSize: '12px', color: 'var(--color-gray-500)', marginTop: '6px', textAlign: 'center' }}>
        {signerName}
      </div>
    </div>
  );
}
